import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import moment from 'moment-hijri';
import {
  CalendarMode,
  HIJRI_MONTHS,
  HijriDateParts,
  formatGregorianFieldValue,
  formatHijriLabel,
  getDefaultHijriParts,
  getHijriDaysInMonth,
  gregorianToHijriParts,
  hijriPartsToGregorian,
  isGregorianInRange,
  isValidGregorianValue,
} from './hijriDate';

interface DateInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  error?: boolean;
  className?: string;
  label?: string | React.ReactNode;
  /** أزرار ميلادي/هجري أصغر فوق الحقل (مثل فلاتر التقارير) */
  compactToggle?: boolean;
  min?: string;
  max?: string;
}

const emitChange = (
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  value: string
) => {
  onChange({
    target: { value },
  } as React.ChangeEvent<HTMLInputElement>);
};

const FIELD_FOCUS =
  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

const dateFieldClass = (hasError: boolean, hijriDisplay = false) =>
  [
    'date-field-input',
    hijriDisplay && 'date-field-input--hijri-display',
    hasError && 'date-field-input--error',
    FIELD_FOCUS,
  ]
    .filter(Boolean)
    .join(' ');

const WEEKDAYS = ['ح', 'ن', 'ث', 'ر', 'خ', 'ج', 'س'];

interface HijriCalendarPanelProps {
  parts: HijriDateParts;
  min?: string;
  max?: string;
  onSelect: (parts: HijriDateParts) => void;
}

const HijriCalendarPanel: React.FC<HijriCalendarPanelProps> = ({
  parts,
  min,
  max,
  onSelect,
}) => {
  const [view, setView] = useState(parts);

  useEffect(() => {
    setView(parts);
  }, [parts.year, parts.month, parts.day]);

  const daysInMonth = getHijriDaysInMonth(view.year, view.month);
  const firstWeekday = moment(
    `${view.year}/${view.month}/1`,
    'iYYYY/iM/iD'
  ).day();

  const cells = useMemo(() => {
    const blanks = Array.from({ length: firstWeekday }, () => null);
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    return [...blanks, ...days];
  }, [firstWeekday, daysInMonth, view.year, view.month]);

  const shiftMonth = (delta: number) => {
    const m = moment(`${view.year}/${view.month}/1`, 'iYYYY/iM/iD').add(
      delta,
      'iMonth'
    );
    setView({
      year: m.iYear(),
      month: m.iMonth() + 1,
      day: Math.min(view.day, getHijriDaysInMonth(m.iYear(), m.iMonth() + 1)),
    });
  };

  const trySelectDay = (day: number) => {
    const next = { ...view, day };
    const gregorian = hijriPartsToGregorian(next);
    if (gregorian && isGregorianInRange(gregorian, min, max)) {
      onSelect(next);
    }
  };

  const isDayDisabled = (day: number) => {
    const gregorian = hijriPartsToGregorian({ ...view, day });
    if (!gregorian) return true;
    return !isGregorianInRange(gregorian, min, max);
  };

  const isSelected = (day: number) =>
    parts.year === view.year &&
    parts.month === view.month &&
    parts.day === day;

  const today = getDefaultHijriParts();

  return (
    <div className="p-2" dir="rtl">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          className="p-0.5 rounded hover:bg-gray-100 text-gray-500"
          aria-label="الشهر السابق"
        >
          <ChevronRightIcon className="w-3.5 h-3.5" />
        </button>
        <span className="text-xs font-medium text-gray-900">
          {HIJRI_MONTHS[view.month - 1]} {view.year}
        </span>
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          className="p-0.5 rounded hover:bg-gray-100 text-gray-500"
          aria-label="الشهر التالي"
        >
          <ChevronLeftIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-0.5">
        {WEEKDAYS.map((d) => (
          <div
            key={d}
            className="text-center text-[10px] font-medium text-gray-500 py-0.5"
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, idx) =>
          day === null ? (
            <div key={`empty-${idx}`} />
          ) : (
            <button
              key={day}
              type="button"
              disabled={isDayDisabled(day)}
              onClick={() => trySelectDay(day)}
              className={`
                h-7 w-7 mx-auto text-xs rounded transition-colors
                ${isSelected(day)
                  ? 'bg-primary-600 text-white font-medium'
                  : 'text-gray-900 hover:bg-gray-100'}
                ${isDayDisabled(day)
                  ? 'opacity-30 cursor-not-allowed hover:bg-transparent'
                  : ''}
              `}
            >
              {day}
            </button>
          )
        )}
      </div>

      <button
        type="button"
        onClick={() => onSelect(today)}
        className="mt-2 w-full text-[10px] text-gray-600 hover:text-gray-900"
      >
        اليوم
      </button>
    </div>
  );
};

const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  error = false,
  className = '',
  label,
  compactToggle = false,
  min,
  max,
}) => {
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('gregorian');
  const [hijriParts, setHijriParts] = useState(getDefaultHijriParts);
  const [pickerOpen, setPickerOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const gregorianDateRef = useRef<HTMLInputElement>(null);
  const isHijri = calendarMode === 'hijri';

  const borderClass = error ? 'border-red-500' : 'border-gray-300';

  useEffect(() => {
    if (value && isValidGregorianValue(value)) {
      const parts = gregorianToHijriParts(value);
      if (parts) setHijriParts(parts);
    }
  }, [value]);

  useEffect(() => {
    if (!pickerOpen || !isHijri) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [pickerOpen, isHijri]);

  const handleHijriSelect = (parts: HijriDateParts) => {
    setHijriParts(parts);
    const gregorian = hijriPartsToGregorian(parts);
    if (gregorian) emitChange(onChange, gregorian);
    setPickerOpen(false);
  };

  const switchMode = (mode: CalendarMode) => {
    setCalendarMode(mode);
    setPickerOpen(false);
    if (mode === 'hijri' && value && isValidGregorianValue(value)) {
      const parts = gregorianToHijriParts(value);
      if (parts) setHijriParts(parts);
    }
  };

  const openHijriPicker = () => setPickerOpen(true);

  const openGregorianPicker = () => {
    const el = gregorianDateRef.current;
    if (!el) return;
    try {
      el.showPicker();
    } catch {
      el.focus();
      el.click();
    }
  };

  const hijriFieldValue =
    value && isValidGregorianValue(value) ? formatHijriLabel(value) : '';

  const gregorianFieldValue =
    value && isValidGregorianValue(value) ? formatGregorianFieldValue(value) : '';

  const calendarToggle = (size: 'full' | 'compact' | 'small' = 'full') => {
    const wrapClass =
      size === 'full'
        ? 'w-full'
        : size === 'compact'
          ? 'w-[5.5rem]'
          : 'w-[4.25rem]';
    const btnClass =
      size === 'small'
        ? 'flex-1 py-px px-0.5 text-[10px] font-medium leading-none transition-colors'
        : 'flex-1 py-0.5 text-xs font-medium leading-tight transition-colors';

    const segmentBtn = (active: boolean) =>
      `${btnClass} ${
        active
          ? 'bg-primary-600 text-white'
          : 'bg-white text-gray-600 hover:bg-gray-50'
      }`;

    return (
      <div
        className={`flex shrink-0 rounded border overflow-hidden ${wrapClass} ${borderClass}`}
      >
        <button
          type="button"
          onClick={() => switchMode('gregorian')}
          className={segmentBtn(calendarMode === 'gregorian')}
        >
          ميلادي
        </button>
        <button
          type="button"
          onClick={() => switchMode('hijri')}
          className={`${segmentBtn(calendarMode === 'hijri')} border-r border-gray-200`}
        >
          هجري
        </button>
      </div>
    );
  };

  return (
    <div className={`space-y-1 ${className}`} dir="rtl">
      {label ? (
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-sm font-medium text-gray-700 shrink-0">{label}</span>
          {calendarToggle(compactToggle ? 'small' : 'compact')}
        </div>
      ) : compactToggle ? (
        <div className="flex justify-end mb-1">{calendarToggle('small')}</div>
      ) : (
        calendarToggle('full')
      )}

      {isHijri ? (
        <div ref={wrapperRef} className="relative">
          <input
            type="text"
            readOnly
            value={hijriFieldValue}
            placeholder="اختر التاريخ الهجري"
            dir="rtl"
            className={dateFieldClass(error, true)}
            onClick={openHijriPicker}
            onFocus={openHijriPicker}
            aria-label="تاريخ هجري"
          />
          {pickerOpen && (
            <div
              className={`absolute z-50 mt-1 right-0 w-[13.5rem] bg-white rounded-lg shadow-md border ${borderClass}`}
            >
              <HijriCalendarPanel
                parts={hijriParts}
                min={min}
                max={max}
                onSelect={handleHijriSelect}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="relative">
          <input
            type="text"
            readOnly
            value={gregorianFieldValue}
            placeholder="DD/MM/YYYY"
            dir="ltr"
            className={dateFieldClass(error, true)}
            onClick={openGregorianPicker}
            onFocus={openGregorianPicker}
            aria-label="تاريخ ميلادي"
          />
          <input
            ref={gregorianDateRef}
            type="date"
            value={value}
            onChange={onChange}
            min={min}
            max={max}
            tabIndex={-1}
            aria-hidden
            className="absolute w-px h-px opacity-0 pointer-events-none overflow-hidden"
            style={{ left: 0, bottom: 0 }}
          />
        </div>
      )}
    </div>
  );
};

export default DateInput;
